import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FileDownload as DownloadIcon,
  Edit as EditIcon,
  RemoveRedEye as ViewIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import noDataImage from '../../image/logo.png';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: '100vh',
  boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.1)',
  borderRadius: 8,
  overflow: 'auto',
  width: '100%',
}));

const StyledTableHead = styled(TableHead, {
  shouldForwardProp: (prop) => prop !== 'headerColor',
})(({ theme, headerColor }) => ({
  '& .MuiTableCell-head': {
    backgroundColor: headerColor || theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 'bold',
    fontSize: '0.95rem',
    padding: '12px 16px',
  },
}));

const StyledTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'minWidth',
})(({ theme, minWidth }) => ({
  padding: '12px 16px',
  minWidth: minWidth || '120px',
}));

const CustomTablePagination = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const ReusableTable = ({
  columns = [],
  data = [],
  isLoading = false,
  headerColor,
  title = 'Data Table',
  onAddClick,
  onEditClick,
  onDeleteClick,
  onViewClick,
  withPagination = true,
  withActions = true,
  actionResolver,
  customEmptyMessage = 'No data available',
  rowsPerPage = 25,
  showDownloadOption = false,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
}) => {
  const theme = useTheme();
  const [page, setPage] = useState(currentPage - 1);
  const [anchorEl, setAnchorEl] = useState(null);

  
  useEffect(() => {
    setPage(currentPage - 1);
  }, [currentPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage - 1);
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const paginatedData = withPagination && data.length > rowsPerPage
    ? data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : data;

  const handleDownloadExcel = () => {
    const sanitizedTitle = title
      .replace(/[^\w\s]/gi, '')
      .replace(/\s+/g, '_')
      .toLowerCase();
    const fileName = `${sanitizedTitle}.xlsx`;

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, fileName);
    setAnchorEl(null);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getActionSettings = (row) => {
    if (actionResolver) {
      return actionResolver(row);
    }
    return {
      view: true,
      edit: true,
      delete: true,
    };
  };

  return (
    <Paper elevation={2} sx={{ overflow: 'hidden' }}>
      <Box
        p={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h3" fontWeight="bold">
          {title}
        </Typography>
        <Box display="flex" gap={1}>
          {onAddClick && (
            <Tooltip title="Add New">
              <IconButton color="primary" onClick={onAddClick}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
          {showDownloadOption && (
            <>
              <Button
                variant="contained"
                color="success"
                onClick={handleMenuClick}
                endIcon={<DownloadIcon />}
              >
                Download
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={handleDownloadExcel}>
                  Download Excel
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>

      {isLoading || data.length === 0 ? (
        <Box textAlign="center" p={3}>
          <img
            src={noDataImage}
            alt="Loading/No data"
            style={{ maxWidth: '250px', marginBottom: theme.spacing(2) }}
          />
          {isLoading ? (
            <Box sx={{ width: '100%', maxWidth: '300px', margin: '0 auto' }}>
              <LinearProgress />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Loading data... / தரவு ஏற்றப்படுகிறது...
              </Typography>
            </Box>
          ) : (
            <Typography variant="h6" color="textSecondary">
              {customEmptyMessage}
            </Typography>
          )}
        </Box>
      ) : (
        <StyledTableContainer>
          <Table stickyHeader>
            <StyledTableHead headerColor={headerColor}>
              <TableRow>
                {columns.map((column) => (
                  <StyledTableCell
                    key={column.id}
                    align={column.numeric ? 'right' : 'left'}
                    minWidth={column.minWidth}
                  >
                    <Typography variant="h6">{column.label}</Typography>
                  </StyledTableCell>
                ))}
                {withActions && (
                  <StyledTableCell align="center">Actions</StyledTableCell>
                )}
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {paginatedData.map((row, index) => {
                const actionSettings = getActionSettings(row);
                return (
                  <TableRow key={index}>
                    {columns.map((column) => (
                      <StyledTableCell
                        key={`${row.id || index}-${column.id}`}
                        align={column.numeric ? 'right' : 'left'}
                        minWidth={column.minWidth}
                      >
                        {column.render
                          ? column.render(row[column.id], row)
                          : row[column.id]}
                      </StyledTableCell>
                    ))}
                    {withActions && (
                      <StyledTableCell align="center">
                        <Box display="flex" justifyContent="center" gap={1}>
                          {onViewClick && actionSettings.view && (
                            <Tooltip title="View">
                              <Button
                                size="small"
                                color="info"
                                onClick={() => onViewClick(row)}
                                startIcon={<ViewIcon />}
                              >
                                View
                              </Button>
                            </Tooltip>
                          )}
                          {onEditClick && actionSettings.edit && (
                            <Tooltip title="Edit">
                              <Button
                                size="small"
                                sx={{ color: 'orange' }}
                                onClick={() => onEditClick(row)}
                                startIcon={<EditIcon />}
                              >
                                Edit
                              </Button>
                            </Tooltip>
                          )}
                          {onDeleteClick && actionSettings.delete && (
                            <Tooltip title="Delete">
                              <Button
                                size="small"
                                color="error"
                                onClick={() => onDeleteClick(row)}
                                startIcon={<DeleteIcon />}
                              >
                                Delete
                              </Button>
                            </Tooltip>
                          )}
                        </Box>
                      </StyledTableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </StyledTableContainer>
      )}

      {withPagination && data.length > 0 && (
        <CustomTablePagination>
          <Typography variant="body2">
            {`Showing ${page * rowsPerPage + 1} - ${Math.min(
              (page + 1) * rowsPerPage,
              data.length
            )} of ${data.length} entries`}
          </Typography>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={handleChangePage}
            color="primary"
            shape="rounded"
          />
        </CustomTablePagination>
      )}
    </Paper>
  );
};

export default ReusableTable;